import React from 'react';
import { connect } from 'react-redux';
import { legacyInjectIntl } from 'js/wrapper/injectIntl';
import { getFacebookOAuthUrl, getGoogleOAuthUrl, getMicrosoftOAuthUrl, getAlzuraOAuthUrl } from 'helpers/socialMedia';
import tokenManager from '../../services/tokenManager';
import { HANDLE_LOGIN, OAUTH_ERROR, loginRedirect } from '../../actions/LoginActions';
import { getApiDocRedirectTarget, redirectToApiDoc } from '../../helpers/apiDocRedirect';

class LoginSocialMediaButtons extends React.Component {
  constructor(props) {
    super(props);
    this.messageListener = null;
    this.pageShowListener = null;
    this.state = {
      isRedirecting: false,
    };
  }

  componentDidMount() {
    // Set up listener for OAuth popup messages
    this.messageListener = (event) => this.handleOAuthMessage(event);
    window.addEventListener('message', this.messageListener);

    // Reset redirecting state when user returns via back/forward navigation (bfcache)
    this.pageShowListener = () => {
      // Only update if we actually had set the redirect flag
      if (this.state.isRedirecting) {
        this.setState({ isRedirecting: false });
      }

      if (this.props.onOAuthRedirect) {
        this.props.onOAuthRedirect(false);
      }
    };
    window.addEventListener('pageshow', this.pageShowListener);
  }

  componentWillUnmount() {
    // Clean up listener
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }

    if (this.pageShowListener) {
      window.removeEventListener('pageshow', this.pageShowListener);
    }
  }

  handleOAuthMessage = (event) => {
    // Verify origin for security
    if (event.origin !== window.location.origin) {
      console.warn('Received message from unexpected origin:', event.origin);
      return;
    }

    const { data } = event;

    // Check if this is a social auth success message
    if (data && data.type === 'SOCIAL_AUTH_SUCCESS') {
      const { accessToken, refreshToken, user, error } = data;

      if (error) {
        console.error('OAuth authentication failed:', error);
        // Dispatch error to Redux
        this.props.dispatch({
          type: OAUTH_ERROR,
          payload: { error },
        });
        return;
      }

      if (accessToken && user) {
        // Store tokens
        tokenManager.setTokens(accessToken);

        // Dispatch login success to Redux
        this.props.dispatch({
          type: HANDLE_LOGIN + '_FULFILLED',
          payload: { data: user },
        });

        // Optionally trigger redirect or other success actions
        if (this.props.onSuccess) {
          this.props.onSuccess(user);
        } else {
          const apiDocReturnPath = getApiDocRedirectTarget();
          if (apiDocReturnPath) {
            redirectToApiDoc(apiDocReturnPath);
          } else {
            // Important: tokenManager stores tokens in-memory (not persisted), so avoid full page reloads.
            // Use the standard login redirect flow instead.
            this.props.dispatch(loginRedirect());
          }
        }
      }
    }
  };

  handleSocialLogin = (url) => (event) => {
    event.preventDefault();

    // Open OAuth popup window
    const width = 600;
    const height = 700;
    const left = (window.innerWidth - width) / 2 + window.screenLeft;
    const top = (window.innerHeight - height) / 2 + window.screenTop;

    const authWindow = window.open(
      url,
      'socialAuthPopup',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    // Check if popup was blocked
    if (!authWindow || authWindow.closed || typeof authWindow.closed === 'undefined') {
      console.error('OAuth popup was blocked by browser');
      alert('Please allow popups for social login to work');
    }
  };

  renderButton(btnClass, iconClass, label, url, idx) {
    const { showLabels, intl } = this.props;
    const { isRedirecting } = this.state;
    const { messages } = intl;

    // ALZURA OAuth uses a standard redirect flow (same tab). Callback sets cookies and redirects back to the app.
    const isAlzura = btnClass === 'btn-alzura';

    if (isAlzura) {
      return (
        <a
          className={
            'oauth-btn-alzura' +
            (showLabels ? '' : ' oauth-btn-alzura-without-text') +
            (isRedirecting ? ' oauth-btn-alzura-loading' : '')
          }
          href={url}
          onClick={(e) => {
            e.preventDefault();
            if (isRedirecting) return; // Prevent double-click
            this.setState({ isRedirecting: true }, () => {
              // Notify parent component (if callback provided)
              if (this.props.onOAuthRedirect) {
                this.props.onOAuthRedirect(true);
              }
            });
            // Small delay to show the loading state before redirect
            setTimeout(() => {
              window.location.href = url;
            }, 300);
          }}
          key={idx}
          style={{
            pointerEvents: isRedirecting ? 'none' : 'auto',
            opacity: isRedirecting ? 0.6 : 1,
            cursor: isRedirecting ? 'not-allowed' : 'pointer',
          }}
        >
          {isRedirecting ? (
            <span>
              <i className="fa fa-spinner fa-spin m-r-5" />
              {showLabels ? messages.redirecting_to_alzura : ''}
            </span>
          ) : (
            showLabels ? label : ''
          )}
        </a>
      );
    }

    return (
      <button
        className={'btn btn-social m-t-10 ' + btnClass + (idx === 0 ? '' : ' m-l-10')}
        onClick={this.handleSocialLogin(url)}
        key={idx}
        type="button"
      >
        <i className={'fab ' + iconClass + (showLabels ? ' m-r-5' : '')} /> {showLabels ? label : ''}
      </button>
    );
  }

  render() {
    const { appIntl, projectConfig, socialMediaLoginState, intl } = this.props;
    const { messages } = intl;
    const { whiteLabelSettings } = globalThis;
    const res = [];
    let i = 0;

    const state = socialMediaLoginState || whiteLabelSettings.id;

    if (projectConfig.data?.facebook_login_client_id) {
      res.push(
        this.renderButton(
          'btn-facebook',
          'fa-facebook-f',
          'Facebook',
          getFacebookOAuthUrl(projectConfig.data?.facebook_login_client_id, appIntl.urlPrefix, state),
          i++
        )
      );
    }

    if (projectConfig.data?.google_login_client_id) {
      res.push(
        this.renderButton(
          'btn-google',
          'fa-google',
          'Google',
          getGoogleOAuthUrl(projectConfig.data?.google_login_client_id, appIntl.urlPrefix, state),
          i++
        )
      );
    }

    const clientId = projectConfig.data?.microsoft_login_client_id;
    const directoryId = projectConfig.data?.microsoft_login_directory_id;
    // console.log(this.constructor.name, { urlPrefix: appIntl.urlPrefix, state });
    if (clientId && directoryId) {
      res.push(
        this.renderButton(
          'btn-microsoft',
          'fa-microsoft',
          'Microsoft',
          getMicrosoftOAuthUrl(clientId, directoryId, appIntl.urlPrefix, state),
          i++
        )
      );
    }

    // ALZURA OAuth login button
    // Check if ALZURA OAuth is enabled via environment variable

    const alzuraEnabled =
      projectConfig &&
      projectConfig.data &&
      (whiteLabelSettings.alzura_oauth_enabled || whiteLabelSettings.alzuraOAuthEnabled);

    if (alzuraEnabled) {
      res.push(
        this.renderButton(
          'btn-alzura',
          'alzura-icon',
          messages.login_with_alzura || 'Login with ALZURA',
          getAlzuraOAuthUrl(appIntl.urlPrefix),
          i++
        )
      );
    }

    return <React.Fragment>{res}</React.Fragment>;
  }
}

LoginSocialMediaButtons.defaultProps = {
  socialMediaLoginState: null,
  showLabels: true,
  onOAuthRedirect: null,
};
const mapStateToProps = state => {
  return {
    appIntl: state.intl,
    projectConfig: state.data.projectConfig,
  };
};

export default legacyInjectIntl(connect(mapStateToProps)(LoginSocialMediaButtons));
